import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { SupportModalBlockComponent } from '@lib/components/support-class';
import { AswServiceModel, DialogClickType, LoginStatusType, PageType } from '@lib/interfaces';
import { CommonLibService } from '@lib/services';
import { Store } from '@ngrx/store';
import { Subscription, take } from 'rxjs';
import { GetBasicReservationInformationResponse } from 'src/sdk-amcmember';
import {
  GetOrderResponseData,
  SendConfirmationMailRequest,
  DefaultService as ServicingApiService,
} from 'src/sdk-servicing';
import { createEmailModalForm } from './email-modal.form';
import { GetMemberInformationResponse } from 'src/sdk-member';
import { IFlyMemberInfo } from 'src/sdk-member';

@Component({
  selector: 'asw-email-modal',
  templateUrl: './email-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailModalComponent extends SupportModalBlockComponent {
  private _subscriptions: Subscription = new Subscription();
  orderData!: GetOrderResponseData;
  subPageId!: string;
  subFunctionId!: string;
  memberInfo!: IFlyMemberInfo | null;
  public formGroup = createEmailModalForm();

  constructor(
    private _common: CommonLibService,
    private readonly _servicingApiService: ServicingApiService,
    public _change: ChangeDetectorRef,
    private _myBookingItineraryService: MyBookingItineraryService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    public change: ChangeDetectorRef,
    private readonly _store: Store
  ) {
    super(_common);
  }

  reload(): void {}

  init() {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.orderData = servicingCommon.getOrder ?? ({} as GetOrderResponseData);
        this._change.markForCheck();
      })
    );

    this.checkDestinationAddress();
    this.change.detectChanges();

    this._subscriptions.add(
      this._myBookingItineraryService.memberInformation$.subscribe((data) => {
        this.memberInfo = data;
        this.checkDestinationAddress();
        this._change.markForCheck();
      })
    );
  }

  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }

  /** (23) 「閉じる(予約確認メール送信モーダル)」ボタン押下時 */
  closeModal() {
    // 1.	予約確認メール送信モーダルを閉じる。
    this.close();
  }

  /** (24)「予約確認メール送信ボタン」ボタン押下時 */
  async clickShareEmail() {
    // 1. メール送信確認の確認ダイアログを表示する。
    // 1.1. 以下の条件でASWDBマスタの動的文言テーブルからメッセージを取得し、取得したメッセージを基にメール送信確認の確認ダイアログ(G02-005)を表示する。
    const result = await this._myBookingItineraryService.openDialogPromiseByMsgId('MSG0398');
    if (result === DialogClickType.CONFIRM) {
      // 2.	入力チェック
      // G03_015 (入力値変換・入力チェック）を呼び出し、入力チェックを行う。
      if (this.formGroup.valid) {
        const getSendConfirmationMailRequest: SendConfirmationMailRequest = this.createSendConfirmationMailRequest(
          this._common.aswServiceStoreService.aswServiceData,
          this.formGroup.get('email')?.value as string
        );

        // 3. 予約確認メール送信APIを呼び出し、予約確認メールの送信を行う。
        this._servicingApiService
          .ordersSendConfirmationMailPost(getSendConfirmationMailRequest)
          .pipe(take(1))
          .subscribe({
            next: (res) => {
              // 3.2.	会員情報取得APIの取得結果が存在する場合、取得結果を破棄する。
              // 3.3. 予約確認メール送信モーダル、旅程共有モーダルを閉じる。
              this.closeShareEmailModal();
            },
            error: (error) => {
              const apiErrorCode = this._common.apiError?.errors?.[0]?.code ?? '';
              // 3.3.	異常終了の場合
              // 3.3.1.	予約確認メール送信APIより、メール送信失敗の旨が返却された場合、当画面にてメール送信に失敗した旨のエラーメッセージを表示する。
              this._common.errorsHandlerService.setRetryableError(PageType.PAGE, {
                // エラーメッセージID
                // 提携: エラーメッセージIDを正しいものに変更(UIUXs2不備)
                errorMsgId: 'W0809',
                // APIエラーコード
                apiErrorCode,
                // 埋め込み文字列置換用情報
                params: [],
              });
              this.closeShareEmailModal();
            },
          });
      }
    }
  }

  closeShareEmailModal() {
    this.closeModal();
    this.payload.closeShareModal();
  }

  createSendConfirmationMailRequest(data?: AswServiceModel, email: string = ''): SendConfirmationMailRequest {
    return {
      orderId: data?.orderId ?? '',
      credential: {
        firstName: data?.firstName ?? '',
        lastName: data?.lastName ?? '',
      },
      emailAddress: email,
    };
  }

  /** (1.7.12) (1.7.13) (1.7.14) 宛名 */
  checkDestinationAddress() {
    let address = this._servicingCommonStoreService.servicingCommon.representativeMail;
    let memberResponse = this.memberInfo;
    // 3)上記以外の場合、空欄
    let re = '';
    // 1)サービシング共通store.代表者メールアドレスが存在する場合、サービシング共通store.代表者メールアドレスを表示する。
    if (address) {
      re = address;
    }
    // 2)上記以外かつユーザ共通情報.data.aswContext.loginStatus ="REAL_LOGIN"(リアルログイン)の場合、予約基本情報取得API応答.data.priorEmailを表示する。
    else if (this.isLoginStatus) {
      re = memberResponse?.profileDetails?.individualInfo?.memberContactInfos?.[0]?.emailAddress ?? '';
    }
    this.formGroup.controls['email'].setValue(re);
    this.formGroup.controls['emailConfirm'].setValue(re);
  }

  get isLoginStatus(): boolean {
    return this._common.aswContextStoreService.aswContextData.loginStatus === LoginStatusType.REAL_LOGIN;
  }
}
